// ---------------------------------------------------------------------------
// SINGLE SOURCE OF TRUTH FOR EVERY BACKEND URL
// ---------------------------------------------------------------------------
// The React app used to hardcode "https://localhost/admin-panel/..." in ~80
// places across 20 files. On this machine that URL can never work, for three
// independent reasons:
//
//   1. SCHEME - the bundled C:/xampp/apache/conf/ssl.crt/server.crt EXPIRED on
//      2019-05-19, is signed with SHA-1, uses a 1024-bit RSA key and carries no
//      Subject Alternative Name. Current Chrome/Edge reject it outright, so the
//      TLS handshake never completes and the browser reports
//      `net::ERR_SSL_PROTOCOL_ERROR` (ERR_CERT_* on older builds).
//      => The API must be called over plain HTTP on a dev box.
//
//   2. PORT - this Apache is configured with `Listen 8080`. It does NOT listen
//      on 80 (Windows http.sys owns that port and answers 500), and 443 is
//      unusable because of point 1.
//
//   3. PATH - the document root is C:/xampp/htdocs, so the app is NOT at
//      "/admin-panel". It lives inside the nested working folder, so the real
//      path is
//        /admin-panel sample sept25/admin-panel sample sept25/admin-panel
//
// Every fetch() and every image URL now goes through this module, so the base
// URL is defined exactly once.
//
// PRODUCTION LAYOUT (verified against the live host)
// ---------------------------------------------------------------------------
// The Admin Module and the Therapist API are BOTH served from the same
// sub-path of the same vhost:
//
//   https://cpassist.traitsolutions.in/admin-panel/                -> Admin Module
//   https://cpassist.traitsolutions.in/admin-panel/pages/*.php     -> Admin pages
//   https://cpassist.traitsolutions.in/admin-panel/api/*.php       -> Admin API
//   https://cpassist.traitsolutions.in/admin-panel/therapist_api/* -> Therapist API
//
// The bare vhost path /therapist_api/ is NOT served - it falls through to the
// SPA catch-all and returns index.html, so a call made there resolves to a
// Document instead of JSON. Verified 5 Oct 2026: the bare path returns
// `<!doctype html>...` while the /admin-panel path returns JSON.
//
// TO REPOINT (staging server, real domain, different port, moved folder)
// ---------------------------------------------------------------------
// Set REACT_APP_API_URL in cpa/.env - that is the single place to change. CRA
// reads .env ONLY at server start, so restart `npm start` after editing it.
// The value MUST include the deploy path segment, e.g.
//     REACT_APP_API_URL=https://cpassist.traitsolutions.in/admin-panel
//
// Leave it blank only when the app is served by the SAME host that serves the
// API (i.e. the production `build/` folder): the base is then derived from
// window.location (origin + deploy path). It MUST be set for `npm start`,
// because the CRA dev server (port 3000) is a different origin from Apache
// (port 8080) and cannot be inferred.
// ---------------------------------------------------------------------------

// The app folder relative to the Apache document root (C:/xampp/htdocs).
// Percent-encoded on purpose: the "CPAssist oct1" and "Therapist Module"
// folder names both contain spaces, and this string is compared against
// Express `req.url` in setupProxy.js, which always sees the encoded form.
const APP_PATH_IN_DOCROOT = [
    "CPAssist oct6",
    "Therapist Module",
    "admin-panel",
].join("/")
    .replace(/ /g, "%20");

// Base URL every API call is built from. No trailing slash.
//
// ── ACTIVE BASE — LOCALHOST (7 Oct 2026) ───────────────────────────────────
// The folder that CONTAINS therapist_api/ locally is
//   https://cpassist.traitsolutions.in/admin-panel
// because apiUrl() appends "therapist_api/<file>.php" to this value, so
//   apiUrl("therapist_api/therapist_login.php")
//     -> "https://cpassist.traitsolutions.in/admin-panel/therapist_api/therapist_login.php"
//
// Verified 7 Oct 2026 by curl against local Apache (`Listen 8080`,
// DocumentRoot C:/xampp/htdocs): that URL answers
// {"status":false,"message":"Token missing"} (200).
//
// ── TO GO BACK TO LIVE PRODUCTION ──────────────────────────────────────────
// Set REACT_APP_API_URL in cpa/.env to
//   https://cpassist.traitsolutions.in/admin-panel
// and restart `npm start`. https://localhost (443) is unusable on this box
// (Apache ssl.crt expired in 2019 -> net::ERR_SSL_PROTOCOL_ERROR) and port 80
// is owned by Windows http.sys (answers 500); 8080 over http is the only
// working local port. The "%20" spaces must stay encoded.
const PRODUCTION_ORIGIN =
      "https://cpassist.traitsolutions.in/admin-panel";

// Fallback origin, used when REACT_APP_API_URL is blank.
const FALLBACK_ORIGIN = PRODUCTION_ORIGIN;

const trimSlashes = (value) => String(value || "").replace(/\/+$/, "");

// REACT_APP_API_URL is the project's established name (see cpa/.env);
// REACT_APP_API_BASE is accepted as an alias for one-off CI builds.
const buildTimeOverride = trimSlashes(
    process.env.REACT_APP_API_URL || process.env.REACT_APP_API_BASE || ""
);

// Only trustworthy when this page was served by the same host that hosts the
// PHP API. Under `npm start` the page comes from the CRA dev server on :3000,
// so deriving the origin there would point every fetch at :3000 and 404.
//
// NOTE: this returns the bare ORIGIN and must never be the primary source for
// the production base. `window.location.origin` is scheme + host only and
// carries no path, so it resolves API calls to
//   `https://cpassist.traitsolutions.in/therapist_api/*.php` - a path the server
// does not serve. It falls through to the SPA catch-all and returns index.html,
// so every fetch resolved against a Document instead of JSON. Verified live:
// the bare path returns `<!doctype html>...`, while
// `/admin-panel/therapist_api/get_age_groups.php` correctly returns
// `{"status":false,"message":"Token missing"}`.
//
// The deploy path CANNOT be recovered from window.location either: the React
// SPA is mounted at the VHOST ROOT (`/`, `/start-assessment`, `/Goals` all
// serve index.html), so window.location.pathname is a client-side route with no
// relationship to the "/admin-panel" prefix the PHP backend is served under.
// PRODUCTION_ORIGIN is therefore the authoritative value and must win.
const pageOrigin = () => {
    if (typeof window === "undefined" || !window.location) return "";
    if (window.location.protocol === "file:") return "";
    if (process.env.NODE_ENV === "development") return "";
    return trimSlashes(window.location.origin);
};

// Base URL every API call is built from. No trailing slash.
//
// In production this MUST resolve to "https://cpassist.traitsolutions.in/admin-panel"
// so that
//   apiUrl("therapist_api/get_summary.php")
//     -> "https://cpassist.traitsolutions.in/admin-panel/therapist_api/get_summary.php"
//
// PRODUCTION_ORIGIN is listed FIRST, ahead of pageOrigin(), on purpose. pageOrigin()
// only knows scheme+host, so letting it win silently drops the "/admin-panel"
// segment and every call lands on the SPA catch-all instead of the PHP API.
export const API_BASE =
    buildTimeOverride ||
    (process.env.NODE_ENV === 'development'
        ? `/${APP_PATH_IN_DOCROOT}`
        : `${PRODUCTION_ORIGIN || pageOrigin() || FALLBACK_ORIGIN}`);

/**
 * Build a full URL for a path under the app folder.
 *
 *   apiUrl("therapist_api/get_summary.php")
 *     -> "https://cpassist.traitsolutions.in/admin-panel/therapist_api/get_summary.php"
 *
 * `path` may be given with or without a leading slash, and passing a full URL
 * is tolerated (only the path part is kept), so migrating the old hardcoded
 * literals over is a no-op either way.
 */
export const apiUrl = (path) => {
    const clean = String(path == null ? "" : path)
        .replace(/^https?:\/\/[^/]+/i, "") // tolerate a full URL being passed
        .replace(/^\/+/, "");
    return clean ? `${API_BASE}/${clean}` : API_BASE;
};

/** Public URL for a file under /uploads (therapist photos, factsheets, ...). */
export const uploadUrl = (relativePath) => {
    const clean = String(relativePath == null ? "" : relativePath).replace(/^\/+/, "");
    return clean ? `${API_BASE}/uploads/${clean}` : `${API_BASE}/uploads`;
};

/**
 * Resolve an image path returned by the API.
 * The PHP endpoints return paths relative to the app folder
 * ("uploads/images/x.png") so they stay host/port agnostic; a full URL from an
 * already-deployed backend is passed through untouched.
 */
export const assetUrl = (src) => {
    const value = String(src == null ? "" : src).trim();
    if (!value) return "";
    if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:")) return value;
    return `${API_BASE}/${value.replace(/^\/+/, "")}`;
};

/** Backwards-compatible default export. */
export default API_BASE;
